import * as THREE from 'three';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {createTweenAnimation} from './createTweenAnimation';

export const toggleOverlayHingeDoorHandler = ({
    mesh,
    axisPosition,
    doorStopOffsetX = 30,
    fullOpenOffsetRatio = 0.8,
    isRightDoor = false,
    degrees = 90,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh;
    axisPosition: number;
    doorStopOffsetX: number;
    // Ratio (0~1) applied to doorStopOffsetX at the fully open resting
    // position. 0.5 keeps half of the original lateral offset, which is the
    // minimum distance needed so the open door does not touch the side panel.
    fullOpenOffsetRatio?: number;
    isRightDoor?: boolean;
    degrees?: number;
    carcaseThickness?: number;
    animationDelay?: number;
}): AnimationHandler => {
    const openDelay = 200;
    const closeDelay = 200;
    const openDuration = 800;
    const closeDuration = 800;

    const rotation = createTweenAnimation({
        mesh,
        property: 'rotation',
        axis: 'y',
        getTargets: () => {
            const radians = THREE.MathUtils.degToRad(degrees);
            return {
                openTarget: isRightDoor ? radians : -radians,
                closeTarget: 0,
            };
        },
        openDelay,
        closeDelay,
        openDuration,
        closeDuration,
    });

    const outwardSign = isRightDoor ? -1 : 1;
    const finalX =
        axisPosition + outwardSign * doorStopOffsetX * fullOpenOffsetRatio;

    const repositionX = createTweenAnimation({
        mesh,
        property: 'position',
        axis: 'x',
        getTargets: () => ({
            openTarget: finalX,
            closeTarget: axisPosition,
        }),
        openDelay,
        closeDelay,
        openDuration,
        closeDuration,
    });

    // Negative: the door clears the side panel toward -y in this scene.
    // Uses the pulse mode so the excursion is purely additive on top of the
    // live y value and retracts exactly to the resting forward gap that the
    // attach-time close snap establishes (see createTweenAnimation PulseParams).
    const clearance = -18;

    const forwardClearance = createTweenAnimation({
        mesh,
        property: 'position',
        axis: 'y',
        mode: 'pulse',
        getAmplitude: () => clearance,
        openDelay,
        closeDelay,
        openDuration,
        closeDuration,
    });

    return {
        open: [rotation.open, repositionX.open, forwardClearance.open],
        close: [rotation.close, repositionX.close, forwardClearance.close],
    };
};
